import { useMemo, useState } from 'react';

import {
  ENVIRONMENTS_POLLING_INTERVAL,
  useEnvironmentList,
} from '@/react/portainer/environments/queries/useEnvironmentList';
import {
  AppliedPolicyStatusType,
  EnvironmentGroupId,
} from '@/react/portainer/environments/types';
import { Policy, PolicyId } from '@/react/portainer/policies/types';
import { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';
import { AttachmentsDatatableDescription } from '@/react/portainer/policies/DetailView/AttachmentsDataTables/AttachmentsDatatableDescription';

import { useTableState } from '@@/datatables/useTableState';
import {
  createPersistedStore,
  refreshableSettings,
  hiddenColumnsSettings,
} from '@@/datatables/types';
import { Datatable } from '@@/datatables/Datatable';
import { Widget } from '@@/Widget';

import { EnvironmentTableSettings } from '../types';

import { getEnvironmentColumns } from './columns';
import { getStatusDisplayLabel } from './statusUtils';

interface Props {
  policy?: Policy;
  group: EnvironmentGroup;
  isPolicyLoading: boolean;
  statusFilter?: AppliedPolicyStatusType;
  /** Search value from unified search bar */
  search?: string;
}

function getStorageKey(policyId: PolicyId, groupId: EnvironmentGroupId) {
  return `policy-attachments-group-datatable-${policyId}-${groupId}`;
}

export function AttachmentsDataTable({
  policy,
  group,
  isPolicyLoading,
  statusFilter,
  search = '',
}: Props) {
  const storageKey = getStorageKey(policy?.Id ?? 0, group.Id);
  const settingsStore = createPersistedStore<EnvironmentTableSettings>(
    storageKey,
    'name',
    (set) => ({
      ...refreshableSettings(set),
      ...hiddenColumnsSettings(set),
    })
  );

  const tableState = useTableState(settingsStore, storageKey);

  const [page, setPage] = useState(0);

  const environmentsQuery = useEnvironmentList(
    {
      nameFilter: search,
      excludeSnapshots: true,
      policy: true,
      pageLimit: tableState.pageSize,
      page: page + 1,
      groupIds: [group.Id],
      policyIds: [policy?.Id ?? 0],
      // Pass statusFilter to API for server-side filtering
      policyStatus: statusFilter ? [statusFilter] : undefined,
      // Pass sort parameters for server-side sorting (only when sorting by name)
      sort: tableState.sortBy?.id === 'name' ? 'Name' : undefined,
      order: getSortOrder(tableState.sortBy),
    },
    {
      enabled: !!policy,
      refetchInterval: ENVIRONMENTS_POLLING_INTERVAL,
    }
  );
  const {
    environments,
    totalCount,
    isLoading: isEnvironmentsLoading,
  } = environmentsQuery;

  const envColumns = useMemo(
    () => getEnvironmentColumns(policy?.Type, policy?.EnvironmentType),
    [policy?.Type, policy?.EnvironmentType]
  );

  // Hide empty groups when a filter is applied to avoid scrolling past empty tables
  const shouldHideEmptyGroup =
    statusFilter && !isEnvironmentsLoading && totalCount === 0;

  if (shouldHideEmptyGroup) {
    return null;
  }

  const emptyLabel = statusFilter
    ? `No environments with "${getStatusDisplayLabel(
        statusFilter
      )}" status in this group`
    : 'No environments in this group';

  return (
    // manually wrap the datatable in a widget to control padding
    <Widget>
      <Datatable
        noWidget
        key={group.Id ?? group.Name}
        title={group.Name}
        description={<AttachmentsDatatableDescription size={group.Size || 0} />}
        dataset={environments}
        columns={envColumns}
        settingsManager={tableState}
        disableSelect
        emptyContentLabel={emptyLabel}
        data-cy={`policy-attachments-group-${group.Id}`}
        isLoading={isPolicyLoading || isEnvironmentsLoading}
        totalCount={totalCount}
        isServerSidePagination
        page={page}
        onPageChange={setPage}
        includeSearch={false}
      />
    </Widget>
  );
}

function getSortOrder(sortBy?: {
  id: string;
  desc: boolean;
}): 'asc' | 'desc' | undefined {
  if (sortBy?.id !== 'name') {
    return undefined;
  }
  return sortBy.desc ? 'desc' : 'asc';
}
